import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { addYears, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { toCurrency, toPercent } from "@helpers";
import {
  GainLossTypography,
  GainLossPercents,
  SecuritiesChart,
  SecuritiesChartType,
} from "@components";
import { ComparisonValue } from "@components/Chart/BaseChartTools";
import { useRangeSelectControl } from "@hooks";
import {
  InvestmentsChartDataStatistic,
  useInvestmentsChartData,
} from "@api/v1/hooks/performance/useInvestmentsChartData";
import { MandatorTypography } from "@components/MandatorTypography";

const SubtitleContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const GainLossPercentsStyled = styled(GainLossPercents)`
  margin-left: ${({ theme }) => theme.spacing(2)};
`;

const SecuritiesChartStyled = styled(SecuritiesChart)`
  ${({ theme }) => theme.breakpoints.down("sm")} {
    box-shadow: none;
  }
`;

interface Props {
  accountIds: number[];
  rangeId: string;
  rangeFirstDate?: Date;
  showComparisonSelector?: boolean;
  onStatistic?: (statistic: InvestmentsChartDataStatistic) => void;
}

export const DepotsDashboardChart = ({
  accountIds,
  rangeId,
  rangeFirstDate,
  showComparisonSelector,
  onStatistic,
}: Props) => {
  const { t } = useTranslation("");
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const startDate = startOfDay(addYears(new Date(), -1));
  const rangeProps = useRangeSelectControl({
    id: rangeId,
    firstDate: rangeFirstDate ?? startDate,
  });
  const { range } = rangeProps;
  const chartStartDate = range?.startDate ?? startDate;
  const chartEndDate = range?.endDate;
  const [chartType, setChartType] = useState<SecuritiesChartType>("valuation");
  const [comparedTicker, setComparedTicker] = useState<ComparisonValue>();

  const {
    data: chartData,
    statistic,
    typesWithData,
    isFetching,
  } = useInvestmentsChartData(
    accountIds,
    chartStartDate,
    chartEndDate,
    chartType,
    comparedTicker?.value
  );

  useEffect(() => {
    onStatistic?.(statistic);
  }, [statistic, onStatistic]);

  return (
    <>
      <SecuritiesChartStyled
        isFetching={isFetching}
        assetTypes={typesWithData}
        drawLines={!isSm}
        title={
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            gap={2}
            mb={isSm ? 2.5 : 0}
          >
            <MandatorTypography variant="h2">
              {chartType === "performance"
                ? toPercent(statistic.performance ?? 0)
                : toCurrency(statistic.portfolioValue ?? 0, "EUR")}
            </MandatorTypography>
            <GainLossPercentsStyled
              size="small"
              value={statistic.changeToday ?? 0}
            />
          </Box>
        }
        chartType={chartType}
        currency="EUR"
        subtitle={
          <SubtitleContainer>
            <GainLossTypography
              variant="body1"
              value={
                chartType === "performance"
                  ? (statistic.portfolioValue ?? 0)
                  : (statistic.investedCapital ?? 0)
              }
            >
              {t(`components.depotsStatistic.chart.subheader.${chartType}`)}{" "}
              {toCurrency(
                chartType === "performance"
                  ? (statistic.portfolioValue ?? 0)
                  : (statistic.investedCapital ?? 0),
                "EUR"
              )}
            </GainLossTypography>
          </SubtitleContainer>
        }
        data={chartData}
        comparedTicker={comparedTicker}
        onComparedTickerChange={setComparedTicker}
        onChartTypeChange={(value: SecuritiesChartType) => setChartType(value)}
        showChartTypeTabs
        showRangeSelector
        rangeProps={rangeProps}
        showComparisonSelector={showComparisonSelector}
      />
    </>
  );
};
